import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { startWith, switchMap, take, tap } from 'rxjs/operators';

import { ApiService, BreakpointObserverService, FocusService } from '@core';
import { MatDialog } from '@angular/material/dialog';
import { ProductDetails } from '@models';
import { WishlistInfoDialogComponent } from '@shared';
import { WishlistProductsService } from './wishlist-products.service';

@Injectable({
  providedIn: 'root',
})
export class WishlistService {
  private readonly dialog = inject(MatDialog);
  private readonly apiService = inject(ApiService);
  private readonly focusService = inject(FocusService);
  private readonly isTabletOrLower = inject(BreakpointObserverService).isTabletOrLower;
  private readonly dataService = inject(WishlistProductsService);
  private readonly popUpStyles = computed(() =>
    this.isTabletOrLower()
      ? {
          width: '80vw',
          height: '300px',
          position: { top: '64px', right: '10vw' },
        }
      : {
          width: '400px',
          height: '300px',
          position: { top: '64px', right: '100px' },
        },
  );

  fetchWishlistIdsTrigger = new Subject<void>();
  wishlistProductIds = signal<number[]>([]);

  constructor() {
    this.fetchWishlistIdsTrigger
      .asObservable()
      .pipe(
        startWith(true),
        switchMap(() => this.wishlistProductIdsRequest()),
      )
      .subscribe((ids) => {
        this.wishlistProductIds.set(ids);
      });
  }

  toggleWishlist(product: ProductDetails): void {
    if (this.wishlistProductIds()?.includes(product.productId)) {
      this.apiService
        .delete(`wishlist/${product.productId}/remove`)
        .pipe(
          take(1),
          tap(() => this.openPopup(this.buildRemovedMessage(product.name))),
        )
        .subscribe(() => this.fetchWishlistIdsTrigger.next());

      this.optimisticRemoveProductFromCache(product);
    } else {
      this.apiService
        .post('wishlist/add', { productId: product.productId })
        .pipe(
          take(1),
          tap(() => this.openPopup(this.buildAddedMessage(product.name))),
        )
        .subscribe(() => this.fetchWishlistIdsTrigger.next());

      this.optimisticAddProductToCache(product);
    }
  }

  private optimisticAddProductToCache(product: ProductDetails): void {
    this.wishlistProductIds.update((ids) => [...ids, product.productId]);
    this.dataService.products.update((products) => [...products, product]);
    this.dataService.totalCount.update((currCount) => currCount + 1);
  }

  private optimisticRemoveProductFromCache(product: ProductDetails): void {
    this.wishlistProductIds.update((ids) => ids.filter((id) => id !== product.productId));
    this.dataService.products.update((products) => products.filter((p) => p.productId !== product.productId));
    this.dataService.totalCount.update((currCount) => currCount - 1);
  }

  private wishlistProductIdsRequest(): Observable<number[]> {
    return this.apiService.get('wishlist/product-ids');
  }

  private openPopup(message: string): void {
    const dialogRef = this.dialog.open(WishlistInfoDialogComponent, {
      ...this.popUpStyles(),
      hasBackdrop: false,
      autoFocus: true,
      data: {
        message,
      },
    });

    dialogRef.afterOpened().subscribe(() => {
      this.focusService.removeMatDialogFocus();
    });
  }

  private buildRemovedMessage(productName: string) {
    return `${productName} has been removed out of your wish list.`;
  }

  private buildAddedMessage(productName: string) {
    return `${productName} has been saved to your wish list.`;
  }
}
